// Sign-in with Microsoft Entra External ID, configured from the API's /auth/config, in one of two ways per
// environment. Native sign-in (auth.nativeSignIn): the portal's own screens drive Entra's native authentication
// (auth/native.ts); a visitor without an account sees the sign-in screen, and an invitation link renders its own
// page. Otherwise MSAL's redirect flow: every page but an invitation link sends a visitor without an account
// straight to Microsoft; the invitation page renders first, so it can start sign-in with the invited email filled
// in. When the API has no Entra configured (a test deployment), the portal falls back to the staff key, typed
// once and kept for the browser session.
import { BrowserCacheLocation, InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from "@azure/msal-browser";
import type { CustomAuthAccountData } from "@azure/msal-browser/custom-auth";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { loadAuthConfig, loadConfig, type AuthConfig, type PortalConfig } from "../config";
import { Button, Icon, Input } from "../ui";
import { Splash } from "../shared/Splash";
import { SignInScreen } from "../screens/SignIn";
import { nativeClient, usesNativeSignIn, type NativeClient } from "./native";

export interface AuthState {
  config: PortalConfig;
  auth: AuthConfig;
  mode: "entra" | "staff-key";
  /** The native sign-in client, when this environment signs in on the portal's own screens. */
  native: NativeClient | null;
  /** Null on an invitation link opened without a sign-in: the page then offers to sign in as the invited person. */
  account: { name: string | null; email: string | null } | null;
  staffKey: string | null;
  getToken: () => Promise<string | null>;
  signOut: () => void;
  /** Starts an interactive sign-in and comes back to this page; a login hint fills the email in on Microsoft's page. */
  signIn: (options?: SignInOptions) => void;
  /** Starts an interactive sign-in for a different account (Microsoft's account picker). */
  switchAccount: () => void;
}

export interface SignInOptions {
  loginHint?: string;
  prompt?: string;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth outside AuthProvider");
  return value;
}

const STAFF_KEY = "av-staff-key";
const RETURN_TO = "av-return-to";

/** The invitation page is the one page that renders before sign-in. */
const onInviteLink = () => window.location.pathname.startsWith("/invite/");

/** Remembers this page and goes to Microsoft; the redirect returns to the origin and the page is restored from the key. */
function starter(app: PublicClientApplication, scope: string) {
  return (options?: SignInOptions) => {
    sessionStorage.setItem(RETURN_TO, window.location.pathname + window.location.search);
    void app.loginRedirect({ scopes: [scope], loginHint: options?.loginHint, prompt: options?.prompt });
  };
}

/** Browser errors and page views go to Application Insights when the API names a connection string. */
function startTelemetry(connectionString: string | null) {
  if (!connectionString || (window as unknown as { __avTelemetry?: boolean }).__avTelemetry) return;
  (window as unknown as { __avTelemetry?: boolean }).__avTelemetry = true;
  import("@microsoft/applicationinsights-web").then(({ ApplicationInsights }) => {
    const insights = new ApplicationInsights({ config: { connectionString, enableAutoRouteTracking: true, disableCookiesUsage: true, disableFetchTracking: false } });
    insights.loadAppInsights();
    insights.trackPageView();
    window.addEventListener("error", (e) => insights.trackException({ exception: e.error instanceof Error ? e.error : new Error(String(e.message)) }));
    window.addEventListener("unhandledrejection", (e) => insights.trackException({ exception: e.reason instanceof Error ? e.reason : new Error(String(e.reason)) }));
  }).catch(() => { /* telemetry is optional */ });
}

type Phase = { kind: "loading" } | { kind: "error"; message: string } | { kind: "staff-key"; config: PortalConfig; auth: AuthConfig } | { kind: "ready"; state: AuthState };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const msal = useRef<PublicClientApplication | null>(null);
  // The router's own navigation, so the restored page is the one the routes render (a bare history.replaceState is invisible to it).
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const buildAnonymous = useCallback((config: PortalConfig, auth: AuthConfig, app: PublicClientApplication): AuthState => {
    const signIn = starter(app, auth.scope!);
    return {
      config,
      auth,
      mode: "entra",
      native: null,
      account: null,
      staffKey: null,
      getToken: async () => null,
      signOut: () => { /* nobody is signed in */ },
      signIn,
      switchAccount: () => signIn({ prompt: "select_account" }),
    };
  }, []);

  const buildEntra = useCallback((config: PortalConfig, auth: AuthConfig, app: PublicClientApplication, account: AccountInfo): AuthState => {
    const scope = auth.scope!;
    const signIn = starter(app, scope);
    const getToken = async () => {
      try {
        const result = await app.acquireTokenSilent({ scopes: [scope], account });
        return result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          sessionStorage.setItem(RETURN_TO, window.location.pathname + window.location.search);
          await app.acquireTokenRedirect({ scopes: [scope], account });
          return null;
        }
        throw error;
      }
    };
    return {
      config,
      auth,
      mode: "entra",
      native: null,
      account: { name: account.name ?? null, email: (account.idTokenClaims?.email as string | undefined) ?? (account.idTokenClaims?.preferred_username as string | undefined) ?? account.username ?? null },
      staffKey: null,
      getToken,
      signOut: () => {
        sessionStorage.clear();
        // Microsoft's sign-out page asks "which account?" unless told; the login_hint claim (or the username) tells it.
        const logoutHint = (account.idTokenClaims?.login_hint as string | undefined) ?? account.username;
        void app.logoutRedirect({ account, logoutHint, postLogoutRedirectUri: window.location.origin });
      },
      signIn,
      switchAccount: () => signIn({ prompt: "select_account" }),
    };
  }, []);

  /** Native sign-in: signed in when the browser cache holds an account, else the sign-in screen (or the invite page). */
  const buildNative = useCallback((config: PortalConfig, auth: AuthConfig, app: NativeClient, data: CustomAuthAccountData | null): AuthState => {
    const scope = auth.scope!;
    // Back to the page the person is on, signed out: the sign-in screen there, or the invite page.
    const startOver = () => window.location.assign(window.location.pathname + window.location.search);
    const signedOut: Omit<AuthState, "account" | "getToken" | "signOut"> = {
      config,
      auth,
      mode: "entra",
      native: app,
      staffKey: null,
      signIn: startOver,
      switchAccount: () => {
        void (async () => {
          await data?.signOut();
          startOver();
        })();
      },
    };
    if (!data) return { ...signedOut, account: null, getToken: async () => null, signOut: () => { /* nobody is signed in */ } };
    const claims = data.getClaims();
    const account = data.getAccount();
    return {
      ...signedOut,
      account: { name: account.name ?? null, email: (claims?.email as string | undefined) ?? account.username ?? null },
      getToken: async () => {
        const result = await data.getAccessToken({ forceRefresh: false, scopes: [scope] });
        if (result.isCompleted() && result.data) return result.data.accessToken;
        // The sign-in ran out (no refresh token, or Entra refused it): sign in again on this page.
        if (result.error?.isCurrentAccountNotFound() || result.error?.errorData?.error === "invalid_grant") {
          await data.signOut();
          startOver();
          return null;
        }
        throw new Error("Couldn’t refresh your sign-in. Check your connection and try again.");
      },
      signOut: () => {
        void (async () => {
          sessionStorage.clear();
          await data.signOut();
          window.location.assign("/");
        })();
      },
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await loadConfig();
        const auth = await loadAuthConfig(config.apiUrl);
        if (cancelled) return;
        startTelemetry(auth.appInsightsConnectionString ?? null);
        // A staff key already in this browser session (the smoke tests set one) takes precedence over Microsoft sign-in.
        if (sessionStorage.getItem(STAFF_KEY) || !auth.enabled || !auth.authority || !auth.clientId || !auth.scope) {
          setPhase({ kind: "staff-key", config, auth });
          return;
        }
        if (usesNativeSignIn(auth)) {
          const app = await nativeClient(config, auth);
          const current = app.getCurrentAccount();
          if (cancelled) return;
          setPhase({ kind: "ready", state: buildNative(config, auth, app, current.isCompleted() ? current.data ?? null : null) });
          return;
        }
        const app = new PublicClientApplication({
          auth: {
            clientId: auth.clientId,
            authority: auth.authority,
            knownAuthorities: [new URL(auth.authority).host],
            redirectUri: window.location.origin,
            postLogoutRedirectUri: window.location.origin,
            navigateToLoginRequestUrl: false,
          },
          cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
        });
        msal.current = app;
        await app.initialize();
        const result = await app.handleRedirectPromise();
        if (cancelled) return;
        let account = result?.account ?? app.getActiveAccount() ?? app.getAllAccounts()[0] ?? null;
        if (result?.account) {
          app.setActiveAccount(result.account);
          const returnTo = sessionStorage.getItem(RETURN_TO);
          sessionStorage.removeItem(RETURN_TO);
          if (returnTo && returnTo !== window.location.pathname + window.location.search) {
            navigateRef.current(returnTo, { replace: true });
          }
        }
        if (!account) {
          if (onInviteLink()) {
            setPhase({ kind: "ready", state: buildAnonymous(config, auth, app) });
            return;
          }
          sessionStorage.setItem(RETURN_TO, window.location.pathname + window.location.search);
          await app.loginRedirect({ scopes: [auth.scope] });
          return;
        }
        app.setActiveAccount(account);
        account = app.getActiveAccount()!;
        setPhase({ kind: "ready", state: buildEntra(config, auth, app, account) });
      } catch (error) {
        if (!cancelled) setPhase({ kind: "error", message: error instanceof Error ? error.message : String(error) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildEntra, buildAnonymous, buildNative]);

  const staffReady = useCallback((config: PortalConfig, auth: AuthConfig, key: string) => {
    sessionStorage.setItem(STAFF_KEY, key);
    setPhase({
      kind: "ready",
      state: {
        config,
        auth,
        mode: "staff-key",
        native: null,
        account: { name: "AutoVouch staff", email: null },
        staffKey: key,
        getToken: async () => null,
        signOut: () => {
          sessionStorage.removeItem(STAFF_KEY);
          window.location.assign("/");
        },
        signIn: () => { /* no Microsoft sign-in on this environment */ },
        switchAccount: () => {
          sessionStorage.removeItem(STAFF_KEY);
          window.location.assign("/");
        },
      },
    });
  }, []);

  useEffect(() => {
    if (phase.kind === "staff-key") {
      const key = sessionStorage.getItem(STAFF_KEY);
      if (key) staffReady(phase.config, phase.auth, key);
    }
  }, [phase, staffReady]);

  const value = useMemo(() => (phase.kind === "ready" ? phase.state : null), [phase]);

  if (phase.kind === "loading") return <Splash text="Signing you in…" />;
  if (phase.kind === "error") return <Splash tone="error" text="AutoVouch couldn’t start" detail={phase.message} action={<Button variant="outline" onClick={() => window.location.reload()}>Try again</Button>} />;
  if (phase.kind === "staff-key") return <StaffKeyScreen onSubmit={(key) => staffReady(phase.config, phase.auth, key)} environment={phase.config.environment} />;
  // Native sign-in, nobody signed in: the sign-in screen, wherever they landed (an invitation link has its own page).
  if (value?.native && !value.account && !onInviteLink()) return <AuthContext.Provider value={value}><SignInScreen /></AuthContext.Provider>;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function StaffKeyScreen({ onSubmit, environment }: { onSubmit: (key: string) => void; environment: string }) {
  const [key, setKey] = useState("");
  return (
    <Splash
      text="Staff key"
      detail={`Sign-in isn’t configured on ${environment}. Enter the staff key to continue; it is kept for this browser session only.`}
      action={
        <form onSubmit={(e) => { e.preventDefault(); if (key.trim()) onSubmit(key.trim()); }} style={{ display: "flex", gap: 10, alignItems: "flex-end", width: "100%", maxWidth: 420 }}>
          <Input type="password" label="Staff key" value={key} onChange={(e) => setKey(e.target.value)} autoFocus containerStyle={{ flex: 1 }} />
          <Button type="submit" disabled={!key.trim()} leadingIcon={<Icon name="log-in" size={16} />}>Continue</Button>
        </form>
      }
    />
  );
}
